import type { WeeklyDay, WeeklyDayChange, WeeklyStatus } from '../../../lib/weeklyApi';
import { TaskListRow, weeklyChangeRow } from '../../tasks/TaskListRow';
import { WEEKLY_STATUS_META } from './weeklyStatusMeta';
import { WeeklyStatusLabel } from './WeeklyStatusVisual';
import { formatDayLabel } from './weeklyWeek';
import { useI18n } from '../../../contexts/I18nContext';

interface Props {
  days: WeeklyDay[];
  /** When non-empty, only transitions landing in these statuses are shown. */
  visibleStatuses: Set<WeeklyStatus>;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * One transition: the shared task row, with the move spelled out underneath —
 * "din To Do în Finalizat", the two buckets as chips in the same colours the
 * status filter uses (the origin muted, the destination coloured). A task
 * created straight into a bucket has no origin and says so instead.
 */
function ChangeRow({ change, userName }: { change: WeeklyDayChange; userName: string }) {
  const { t } = useI18n();
  const toLabel = t(WEEKLY_STATUS_META[change.to].labelKey);
  const fromLabel = change.from ? t(WEEKLY_STATUS_META[change.from].labelKey) : null;
  const toChip = <WeeklyStatusLabel status={change.to} size={11} />;
  return (
    <TaskListRow task={weeklyChangeRow(change, userName)} hideAssignee>
      <span
        className="text-[11px] text-gray-500"
        title={
          fromLabel
            ? t('weekly.movedFromTo', { from: fromLabel, to: toLabel })
            : t('weekly.createdAs', { to: toLabel })
        }
      >
        {change.from ? (
          <span className="inline-flex flex-wrap items-center gap-1">
            {t('weekly.fromWord')} <WeeklyStatusLabel status={change.from} size={11} muted />{' '}
            {t('weekly.toWord')} {toChip}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1">
            {t('weekly.created').toLowerCase()} → {toChip}
          </span>
        )}
      </span>
    </TaskListRow>
  );
}

/**
 * By-day timeline (CLE-74 §2): for each day Mon→Sun, the status transitions that
 * happened that day, grouped per member — the member as a heading, then their
 * rows edge to edge under it (no avatar gutter, no avatar repeated on every
 * row: the heading already says who). Days with no activity render a muted
 * "No status changes" line so the full week skeleton is always visible.
 */
export function WeeklyDayTimeline({ days, visibleStatuses }: Props) {
  const { t } = useI18n();
  const filterChanges = (changes: WeeklyDayChange[]) =>
    visibleStatuses.size === 0 ? changes : changes.filter((c) => visibleStatuses.has(c.to));

  return (
    <div className="space-y-4">
      {days.map((day) => {
        const usersWithChanges = day.users
          .map((u) => ({ ...u, changes: filterChanges(u.changes) }))
          .filter((u) => u.changes.length > 0);

        return (
          <section key={day.date} className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-3 font-semibold capitalize text-gray-800">
              {formatDayLabel(day.date)}
            </h2>

            {usersWithChanges.length === 0 ? (
              <p className="text-sm text-gray-500">{t('weekly.noStatusChanges')}</p>
            ) : (
              <div className="space-y-4">
                {usersWithChanges.map((u) => (
                  <div key={u.user.id}>
                    <p className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                      {u.user.avatarUrl ? (
                        <img
                          src={u.user.avatarUrl}
                          alt=""
                          className="h-6 w-6 shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-[11px] font-semibold text-blue-700">
                          {initials(u.user.name)}
                        </span>
                      )}
                      <span className="truncate">{u.user.name}</span>
                    </p>
                    <ul className="mt-1.5 overflow-hidden rounded-md border border-gray-200">
                      {u.changes.map((c, i) => (
                        <ChangeRow
                          key={`${c.task.id}-${c.at}-${i}`}
                          change={c}
                          userName={u.user.name}
                        />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
